import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useBoolean } from 'usehooks-ts';
import { Box } from '@mui/material';
import { DataGridPro, GridActionsCellItem, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';

import {
  DATA_GRID_COLUMN_TYPE,
  SORTABLE_COLUMN_TYPES,
  FILTERABLE_COLUMN_TYPES,
  DATE_COLUMN_TYPES,
  HEADER_HEIGHT,
  DATA_GRID_PAGE_SIZE_OPTIONS,
} from 'constants/components';
import { SORT_OPTIONS } from 'constants/common';
import { getFilteredList, updateDataGridFilterModel } from 'helper/dataGrid';
import { toDate } from 'helper/DateLib';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { EmptyListOverlay, ColorButton, menuStyles } from 'components/simple';
import { ButtonConfirmIcon, ButtonTopIcon, MoreRedSmallIcon } from 'assets/icons';

import { SortBaseIcon, SortAscIcon, SortDescIcon } from './SortIcons';
import { InfiniteScrollFooter } from './InfiniteScrollFooter';
import { ColumnHeaderFilterSlot } from './ColumnHeaderFilterSlot';

const getDefaultRowSpacing = (params) => ({
  top: params.isFirstVisible ? 8 : 4,
  bottom: params.isLastVisible ? 8 : 4,
});

const styles = {
  dataGridPopper: (theme) => ({
    '.MuiPaper-root': {
      ...menuStyles.panel(theme),
      marginBlock: 1,

      '.MuiList-root': {
        padding: 'unset',

        '.MuiMenuItem-root': {
          borderBottom: `1px solid ${theme.palette.colors.darkPale}`,
          gap: 1.5,
          height: 48,
          padding: 2,

          '.MuiListItemText-primary': {
            maxWidth: 'unset',
          },

          '&:hover': {
            backgroundColor: theme.palette.colors.lightPale,

            '.MuiTypography-body1': {
              fontWeight: 600,
            },
          },
        },

        '.MuiListItemIcon-root': menuStyles.listItemIcon(theme),
      },
    },
  }),
  dataGrid: ({ palette, spacing, typography }) => ({
    border: 'none',
    width: '100%',
    scrollMarginTop: `${HEADER_HEIGHT + 16}px`,
    [`& .${gridClasses.virtualScrollerRenderZone}`]: {
      width: '100%',
    },
    '& .MuiDataGrid-cellOffsetLeft, & .MuiDataGrid-cellEmpty': {
      display: 'none',
    },
    [`& .${gridClasses.row}`]: {
      width: '100%',
      backgroundColor: `${palette.colors.lightPale} !important`,
      border: `1px solid ${palette.colors.darkPale}`,
      borderRadius: 2.5,
    },
    [`& .${gridClasses.cell}`]: {
      border: 'none',
      ...typography.body2,
      '&:first-of-type': {
        paddingLeft: 2,
      },
      '&:focus, &:focus-within': {
        outline: 'none !important',
      },
      '&.wrap-text-cell': {
        whiteSpace: 'wrap !important',
        wordBreak: 'break-word',
      },
    },
    [`& .${gridClasses['cell--editing']}`]: {
      backgroundColor: 'transparent !important',
      boxShadow: 'unset !important',
      overflow: 'visible !important',
      paddingInline: '10px !important',
      '.MuiOutlinedInput-notchedOutline': {
        border: 'none !important',
      },
    },
    [`& .${gridClasses.actionsCell} .MuiIconButton-root`]: {
      padding: 'unset',
      color: 'inherit',
      svg: {
        width: 24,
        height: 24,
      },
    },
    [`& .${gridClasses.columnHeaders}`]: {
      backgroundColor: palette.primary.main,
      border: `1px solid ${palette.primary.main}`,
      borderRadius: 2.5,
    },
    [`& .${gridClasses.columnHeader}`]: {
      backgroundColor: 'transparent',
      outline: 'none !important',
      '&:first-of-type': {
        paddingLeft: 2,
      },
    },
    [`& .${gridClasses.columnHeaders} .${gridClasses['row--borderBottom']} > *`]: {
      borderBottom: 'none',
    },
    [`& .${gridClasses.columnHeaderTitleContainer}`]: {
      gap: 0,
      '.MuiIconButton-root': {
        padding: `0 0 0 ${spacing(0.5)}`,
        backgroundColor: 'transparent',
      },
    },
    [`& .${gridClasses.columnHeaderTitle}`]: {
      ...typography.body2,
      whiteSpace: 'normal',
      textAlign: 'center',
      lineHeight: 1.5,
      fontWeight: 700,
      color: palette.text.secondary,
    },
    [`& .${gridClasses.columnSeparator}`]: {
      display: 'none',
    },
    [`& .${gridClasses.columnHeaderCheckbox}`]: {
      '.MuiButtonBase-root': {
        padding: 0,
        backgroundColor: palette.common.white,
        color: palette.common.white,
        borderRadius: '5px',
        border: `2px solid ${palette.common.white}`,
        '&.Mui-disabled': {
          color: palette.common.white,
          opacity: 0.5,
        },
        '&.Mui-checked': {
          backgroundColor: palette.colors.coralDefault,
          borderColor: palette.common.white,
        },
      },
    },
    [`& .${gridClasses.cellCheckbox}`]: {
      '.MuiButtonBase-root': {
        padding: 0,
        backgroundColor: palette.common.white,
        color: palette.common.white,
        borderRadius: '5px',
        border: `2px solid ${palette.colors.darkPale}`,
        '&.Mui-disabled': {
          color: palette.common.white,
          opacity: 0.5,
        },
        '&.Mui-checked': {
          backgroundColor: palette.colors.coralDefault,
          borderColor: palette.colors.coralDefault,
        },
      },
    },
    [`& .${gridClasses.iconButtonContainer}`]: {
      width: 'auto !important',
      visibility: 'visible',
      '.MuiDataGrid-sortIcon': {
        width: 16,
        height: 16,
        opacity: '1 !important',
        transition: 'unset !important',
        visibility: 'visible !important',
        color: palette.text.secondary,
      },
    },
    [`& .${gridClasses.columnHeaders} .${gridClasses.columnHeader} .${gridClasses.sortButton}`]: {
      opacity: 1,
    },
  }),
  dataGridWithTransparentHeader: ({ palette }) => ({
    [`& .${gridClasses.columnHeaders}`]: {
      backgroundColor: palette.common.white,
      borderColor: palette.common.white,
      marginTop: '-1px',
      borderRadius: 'unset',
    },
    [`& .${gridClasses.columnHeaderTitle}`]: {
      color: palette.text.primary,
    },
    [`& .${gridClasses.iconButtonContainer}`]: {
      '.MuiDataGrid-sortIcon': {
        color: palette.text.primary,
      },
    },
  }),
  dataGridWithClickableRows: {
    [`& .${gridClasses.row}`]: {
      cursor: 'pointer',
    },
  },
  truncatedCell: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'inherit',
  },
  icon: ({ palette }) => ({
    width: 24,
    height: 24,
    padding: 0.5,
    color: palette.common.white,
  }),
  infiniteScrollDataGrid: (heightOffset) => ({
    height: `calc(100vh - ${heightOffset}px)`,
    width: '100%',
  }),
  goToTopButton: ({ palette }) => ({
    position: 'fixed',
    bottom: 50,
    right: 50,
    zIndex: 1000,
    backgroundColor: palette.info.main,
    color: palette.common.white,
    '&.MuiIconButton-root': {
      width: 40,
      height: 40,
      padding: 0.5,
    },
  }),
};

const getDataGridColumn = (
  { getActions, width, xxlWidth = width, type = DATA_GRID_COLUMN_TYPE.STRING, ...data },
  iXxlWidth,
) => {
  const columnWidth = iXxlWidth ? xxlWidth : width;

  const defaultConfig = {
    type,
    display: 'flex',
    align: 'center',
    headerAlign: 'center',
    aggregable: false,
    editable: false,
    filterable: FILTERABLE_COLUMN_TYPES.includes(type),
    groupable: false,
    hideable: false,
    pinnable: false,
    resizable: false,
    disableReorder: true,
    sortable: SORTABLE_COLUMN_TYPES.includes(type),
    width: columnWidth,
    ...(type !== DATA_GRID_COLUMN_TYPE.ACTIONS && {
      renderCell: (params) => <Box sx={styles.truncatedCell}>{params.formattedValue ?? params.value}</Box>,
    }),
  };

  const defaultActionsConfig = {
    width: columnWidth || 50,
    display: 'flex',
    align: 'left',
    ...(getActions && {
      getActions: (params) =>
        (getActions(params) || []).map((actionData) => <GridActionsCellItem key={actionData.label} {...actionData} />),
    }),
  };

  const defaultDateConfig = {
    valueGetter: (value) => toDate(value),
  };

  return {
    ...defaultConfig,
    ...(type === DATA_GRID_COLUMN_TYPE.ACTIONS && defaultActionsConfig),
    ...(DATE_COLUMN_TYPES.includes(type) && defaultDateConfig),
    ...data,
  };
};

export const DataGridTable = ({
  apiRef: externalApiRef,
  page = 0,
  pageSize,
  rowCount,
  columns,
  rows,
  isLoading = false,
  isFetching,
  sortModel = null,
  selectedRows = [],
  isDisabledSelection = false,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  onTableSort,
  setSelectedRows = () => {},
  filterMode = 'server',
  filterModelItems = [],
  onFilterModelChange = () => {},
  isFilteredList = false,
  itemsName,
  withTransparentHeader = false,
  onRowClick = undefined,
  customStyles = {},
  excludedRowIds = [],
  isInfiniteScroll = false,
  hasNextPage = false,
  onLoadMore = undefined,
  sortingOrder = [SORT_OPTIONS.ASC, SORT_OPTIONS.DESC],
  sortingMode = 'server',
  infiniteScrollHeightOffset = 250,
  rowsPerPageOptions = DATA_GRID_PAGE_SIZE_OPTIONS.DEFAULT,
  paginationMode = 'server',
  checkboxSelection = false,
  ...tableProps
}) => {
  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef || internalApiRef;
  const { value: showGoToTop, setValue: setShowGoToTop } = useBoolean();
  const { isXxlUp: isXxlWidth } = useBreakpointFlags();

  const isFetchingState = isFetching ?? isLoading;
  const isFilteredTable = !!filterModelItems.length || isFilteredList;
  const isServerSideFiltering = filterMode === 'server';

  // hold the last rowCount while fetching; it drops to 0 mid-refetch and breaks v8 pagination (CCW-457)
  const [settledRowCount, setSettledRowCount] = useState(rowCount);

  if (!isFetchingState && settledRowCount !== rowCount) {
    setSettledRowCount(rowCount);
  }

  const paginationRowCount = isFetchingState ? settledRowCount : rowCount;

  const formattedColumns = useMemo(
    () => columns.map((column) => getDataGridColumn(column, isXxlWidth)),
    [columns, isXxlWidth],
  );

  const filteredRows = useMemo(
    () => (isServerSideFiltering ? rows : getFilteredList(rows, filterModelItems, excludedRowIds)),
    [rows, filterModelItems, isServerSideFiltering, excludedRowIds],
  );

  const paginationModel = useMemo(() => ({ page, pageSize }), [page, pageSize]);
  const rowSelectionModel = useMemo(() => ({ type: 'include', ids: new Set(selectedRows) }), [selectedRows]);
  const dataGridSortModel = useMemo(() => (sortModel ? [sortModel] : []), [sortModel]);

  const handleRowSelectionModelChange = useCallback((model) => setSelectedRows([...model.ids]), [setSelectedRows]);

  const handleRowsScrollEnd = useCallback(() => {
    if (isFetchingState || !hasNextPage) {
      return;
    }

    onLoadMore?.();
  }, [hasNextPage, isFetchingState, onLoadMore]);

  const handleGoToTop = () => {
    apiRef.current?.scroll({ top: 0 });
  };

  const handlePageChange = (newPage) => {
    onPageChange(newPage);
    apiRef.current?.rootElementRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePaginationModelChange = (model) => {
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }

    if (model.page !== page) {
      handlePageChange(model.page);
    }
  };

  const handleFilterModelChange = (field, updatedValue) => {
    handlePageChange(0);
    onFilterModelChange((prev) => updateDataGridFilterModel(prev, field, updatedValue));
  };

  const handleSortModelChange = (newSortModel) => {
    const nextSort = newSortModel[0];
    const nextField = nextSort?.field;
    const nextDirection = nextSort?.sort;
    const currentField = sortModel?.field;
    const currentDirection = sortModel?.sort;

    // ignore no-op sort fires; they reset the page to 0 and break pagination (CCW-457)
    if (nextField === currentField && nextDirection === currentDirection) {
      return;
    }

    handlePageChange(0);
    onTableSort(nextSort);
  };

  useEffect(() => {
    if (!isInfiniteScroll) {
      return undefined;
    }

    return apiRef.current.subscribeEvent('scrollPositionChange', ({ top }) => {
      setShowGoToTop(top > 300);
    });
  }, [isInfiniteScroll, apiRef, setShowGoToTop]);

  return (
    <>
      <DataGridPro
        apiRef={apiRef}
        pagination={!isInfiniteScroll}
        autoHeight={!isInfiniteScroll}
        disableRowSelectionOnClick
        disableColumnSelector
        disableColumnMenu
        checkboxSelection={checkboxSelection}
        disableMultipleRowSelection={!checkboxSelection}
        hideFooterSelectedRowCount
        hideFooter={!isInfiniteScroll && !rows.length && !isFilteredTable}
        sortingOrder={sortingOrder}
        sortingMode={sortingMode}
        filterMode={filterMode}
        paginationMode={paginationMode}
        {...(paginationMode === 'server' && { rowCount: paginationRowCount })}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={rowsPerPageOptions}
        sortModel={dataGridSortModel}
        rowHeight={84}
        columnHeaderHeight={withTransparentHeader ? 48 : 84}
        columns={formattedColumns}
        rows={filteredRows}
        loading={isLoading || (isFetchingState && !filteredRows.length)}
        onSortModelChange={handleSortModelChange}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        onRowClick={onRowClick}
        sx={[
          styles.dataGrid,
          withTransparentHeader && styles.dataGridWithTransparentHeader,
          onRowClick && styles.dataGridWithClickableRows,
          customStyles.dataGrid,
          isInfiniteScroll && styles.infiniteScrollDataGrid(infiniteScrollHeightOffset),
        ]}
        slots={{
          columnSortedAscendingIcon: SortAscIcon,
          columnSortedDescendingIcon: SortDescIcon,
          columnUnsortedIcon: SortBaseIcon,
          moreActionsIcon: MoreRedSmallIcon,
          noResultsOverlay: EmptyListOverlay,
          noRowsOverlay: EmptyListOverlay,
          ...(isInfiniteScroll && {
            footer: InfiniteScrollFooter,
          }),
          columnHeaderFilterIconButton: ColumnHeaderFilterSlot,
        }}
        slotProps={{
          columnHeaderFilterIconButton: {
            formattedColumns,
            filterModelItems,
            onFilterModelChange: handleFilterModelChange,
            withTransparentHeader,
          },
          baseTooltip: { open: false },
          baseCheckbox: {
            disableRipple: true,
            disabled: isDisabledSelection,
            checkedIcon: <Box component={ButtonConfirmIcon} sx={styles.icon} />,
            indeterminate: false,
          },
          basePopper: { sx: styles.dataGridPopper },
          noResultsOverlay: {
            customStyles: customStyles.emptyTable,
            isFiltered: isFilteredTable,
            itemsName,
          },
          noRowsOverlay: {
            customStyles: customStyles.emptyTable,
            isFiltered: isFilteredTable,
            itemsName,
          },
          ...(isInfiniteScroll && {
            footer: { isLoading: isFetchingState },
          }),
        }}
        getRowSpacing={getDefaultRowSpacing}
        {...tableProps}
        {...(isInfiniteScroll && {
          onRowsScrollEnd: handleRowsScrollEnd,
          scrollEndThreshold: 300,
        })}
      />
      {isInfiniteScroll && showGoToTop && (
        <ColorButton icon={ButtonTopIcon} color="info" customStyles={styles.goToTopButton} onClick={handleGoToTop} />
      )}
    </>
  );
};

DataGridTable.propTypes = {
  apiRef: PropTypes.shape({ current: PropTypes.shape({}) }),
  page: PropTypes.number,
  pageSize: PropTypes.number.isRequired,
  rowCount: PropTypes.number.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  sortModel: PropTypes.shape({
    field: PropTypes.string,
    sort: PropTypes.oneOf(Object.values(SORT_OPTIONS)),
  }),
  selectedRows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  checkboxSelection: PropTypes.bool,
  isDisabledSelection: PropTypes.bool,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  isRowSelectable: PropTypes.func,
  onTableSort: PropTypes.func.isRequired,
  setSelectedRows: PropTypes.func,
  sortingMode: PropTypes.oneOf(['client', 'server']),
  paginationMode: PropTypes.oneOf(['client', 'server']),
  filterMode: PropTypes.oneOf(['client', 'server']),
  filterModelItems: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    }),
  ),
  onFilterModelChange: PropTypes.func,
  isFilteredList: PropTypes.bool,
  itemsName: PropTypes.string.isRequired,
  withTransparentHeader: PropTypes.bool,
  onRowClick: PropTypes.func,
  getRowSpacing: PropTypes.func,
  customStyles: PropTypes.shape({
    dataGrid: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    emptyTable: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  }),
  excludedRowIds: PropTypes.arrayOf(PropTypes.string),
  sortingOrder: PropTypes.arrayOf(PropTypes.oneOf(Object.values(SORT_OPTIONS))),
  isInfiniteScroll: PropTypes.bool,
  hasNextPage: PropTypes.bool,
  onLoadMore: PropTypes.func,
  infiniteScrollHeightOffset: PropTypes.number,
};
